import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { productBrand, screenCopy } from "@/constants/Brand"
import { Colors } from "@/constants/Colors"
import { WEB_MOBILE_FRAME_WIDTH } from "@/constants/WebLayout"
import { router } from "expo-router"
import {
  ChevronRight,
  Fingerprint,
  Gem,
  Layers3,
  Network,
  PlayCircle,
  ShieldCheck,
} from "lucide-react-native"
import { createElement, useState } from "react"
import {
  Platform,
  Pressable,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const slides = [
  {
    eyebrow: "Coach",
    title: "Build your personal EQ Skill Tree",
    text: "Choose the soft skills you want to coach and see your next growth level clearly.",
    icon: Network,
    color: Colors.dark.primary,
  },
  {
    eyebrow: "Prove",
    title: "Record real-life moments and prove your growth",
    text: "Log proof from conversations, decisions, repairs, and moments that show real progress.",
    icon: ShieldCheck,
    color: Colors.dark.proof,
  },
  {
    eyebrow: "Own",
    title: "Earn verifiable badges you can share anywhere",
    text: "Build an EMO.coach Passport that turns coached growth into portable evidence.",
    icon: Gem,
    color: Colors.dark.growth,
  },
]

const stars = [
  { left: "8%", top: "9%", size: 2, opacity: 0.7 },
  { left: "18%", top: "27%", size: 3, opacity: 0.35 },
  { left: "32%", top: "14%", size: 2, opacity: 0.5 },
  { left: "58%", top: "8%", size: 3, opacity: 0.55 },
  { left: "76%", top: "21%", size: 2, opacity: 0.45 },
  { left: "89%", top: "12%", size: 2, opacity: 0.75 },
  { left: "12%", top: "46%", size: 2, opacity: 0.35 },
  { left: "84%", top: "48%", size: 3, opacity: 0.4 },
] as const

export default function OnboardingScreen() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const { height: viewportHeight, width: viewportWidth } = useWindowDimensions()
  const isCompactWebHeight = Platform.OS === "web" && viewportHeight < 760
  const carouselHeight = isCompactWebHeight ? 150 : 170
  const slideWidth = Platform.OS === "web" ? WEB_MOBILE_FRAME_WIDTH : Math.max(1, viewportWidth)
  const createAccountHandlers =
    Platform.OS === "web"
      ? ({ onClick: () => router.push("/auth?mode=signup") } as Record<string, () => void>)
      : {}
  const signInHandlers =
    Platform.OS === "web"
      ? ({ onClick: () => router.push("/auth?mode=signin") } as Record<string, () => void>)
      : {}

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: Colors.dark.surface, overflow: "hidden" }}
    >
      <View className="absolute inset-0" style={{ backgroundColor: Colors.dark.surface }} />
      <View
        className="absolute -left-24 top-16 h-72 w-72 rounded-full"
        style={{ backgroundColor: `${Colors.dark.primary}2E` }}
      />
      <View
        className="absolute -right-20 top-48 h-56 w-56 rounded-full"
        style={{ backgroundColor: `${Colors.dark.skill}24` }}
      />
      {stars.map((star, index) => (
        <View
          key={index}
          className="absolute rounded-full"
          style={{
            left: star.left,
            top: star.top,
            height: star.size,
            width: star.size,
            backgroundColor: Colors.dark.textPrimary,
            opacity: star.opacity,
          }}
        />
      ))}
      <VStack className="flex-1 px-6 pb-8 pt-6">
        <TouchableOpacity
          className="self-end rounded-full px-4 py-2"
          onPress={() => router.replace("/(tabs)")}
        >
          <Text className="font-bold" style={{ color: Colors.dark.textSecondary }}>
            {screenCopy.onboarding.skipAction}
          </Text>
        </TouchableOpacity>

        <View className="flex-1 items-center justify-center">
          {!isCompactWebHeight ? (
            <Box
              className="mb-7 h-32 w-32 items-center justify-center rounded-full border"
              style={{
                backgroundColor: `${Colors.dark.primary}1F`,
                borderColor: `${Colors.dark.primary}80`,
                shadowColor: Colors.dark.primary,
                shadowOpacity: 0.72,
                shadowRadius: 32,
                shadowOffset: { width: 0, height: 0 },
              }}
            >
              <Box
                className="absolute h-20 w-20 rounded-full border"
                style={{ borderColor: "rgba(255,255,255,0.12)" }}
              />
              <Fingerprint color={Colors.dark.primary} size={58} strokeWidth={1.6} />
            </Box>
          ) : null}

          {isCompactWebHeight
            ? createElement(
                "div",
                {
                  style: {
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    width: "100%",
                  },
                },
                createElement(
                  "h1",
                  {
                    style: {
                      color: "#FFFFFF",
                      fontSize: 32,
                      fontWeight: 800,
                      lineHeight: "38px",
                      margin: "0 0 8px",
                      maxWidth: 360,
                    },
                  },
                  screenCopy.onboarding.title,
                ),
                createElement(
                  "div",
                  {
                    style: {
                      color: "#FFFFFF",
                      fontSize: 24,
                      fontWeight: 800,
                      lineHeight: "32px",
                      marginBottom: 12,
                    },
                  },
                  "Build. Prove. Own.",
                ),
                createElement(
                  "p",
                  {
                    style: {
                      color: Colors.dark.textPrimary,
                      fontSize: 16,
                      lineHeight: "24px",
                      margin: 0,
                      maxWidth: 360,
                    },
                  },
                  screenCopy.onboarding.subtitle,
                ),
                createElement(
                  "p",
                  {
                    style: {
                      color: Colors.dark.primary,
                      fontSize: 16,
                      fontWeight: 800,
                      lineHeight: "22px",
                      margin: "10px 0 0",
                    },
                  },
                  productBrand.tagline,
                ),
              )
            : null}
          {!isCompactWebHeight ? (
            <>
              <Text className="mb-2 text-center text-4xl font-bold text-white">
                {screenCopy.onboarding.title}
              </Text>
              <Text
                className="mb-4 text-center text-3xl font-bold text-white"
                style={{ lineHeight: 38 }}
              >
                Build. Prove. Own.
              </Text>

              <Text
                className="max-w-sm text-center text-lg leading-7"
                style={{ color: Colors.dark.textPrimary }}
              >
                {screenCopy.onboarding.subtitle}
              </Text>
              <Text
                className="mt-3 text-center text-base font-bold"
                style={{ color: Colors.dark.primary }}
              >
                {productBrand.tagline}
              </Text>
            </>
          ) : null}

          {!isCompactWebHeight ? (
            <HStack className="mt-6 gap-3">
              {[Layers3, PlayCircle, Gem].map((Icon, index) => (
                <Box
                  key={index}
                  className="h-11 w-11 items-center justify-center rounded-full border"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.045)",
                    borderColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <Icon
                    color={index === 1 ? Colors.dark.proof : Colors.dark.textPrimary}
                    size={19}
                  />
                </Box>
              ))}
            </HStack>
          ) : null}
        </View>

        <ScrollView
          testID="onboarding-carousel"
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onMomentumScrollEnd={(event) => {
            setCurrentSlide(Math.round(event.nativeEvent.contentOffset.x / slideWidth))
          }}
          contentContainerStyle={{ width: slideWidth * slides.length }}
          className="mb-5"
          style={{
            flexGrow: 0,
            flexShrink: 0,
            height: carouselHeight,
            marginHorizontal: -24,
            width: slideWidth,
          }}
        >
          {slides.map((slide, index) => {
            const Icon = slide.icon
            return (
              <View
                key={slide.title}
                testID={`onboarding-carousel-slide-${index}`}
                style={{ flexShrink: 0, minWidth: slideWidth, width: slideWidth }}
                className="px-6"
              >
                <Box
                  className="rounded-2xl border p-4"
                  style={{
                    backgroundColor: Colors.dark.surfaceSecondary,
                    borderColor: `${slide.color}33`,
                  }}
                >
                  <HStack className="items-start gap-3">
                    <Box
                      className="h-12 w-12 items-center justify-center rounded-2xl border"
                      style={{
                        backgroundColor: `${slide.color}24`,
                        borderColor: `${slide.color}57`,
                      }}
                    >
                      <Icon color={slide.color} size={24} />
                    </Box>
                    <VStack className="flex-1 gap-1">
                      <Text className="text-xs font-bold" style={{ color: slide.color }}>
                        {slide.eyebrow.toUpperCase()}
                      </Text>
                      <Text className="text-lg font-bold text-white">{slide.title}</Text>
                      <Text
                        className="text-sm leading-5"
                        style={{ color: Colors.dark.textSecondary }}
                      >
                        {slide.text}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              </View>
            )
          })}
        </ScrollView>

        <HStack className="mb-6 justify-center gap-2">
          {slides.map((slide, index) => (
            <View
              key={slide.title}
              className="h-2 rounded-full"
              style={{
                width: currentSlide === index ? 28 : 8,
                backgroundColor:
                  currentSlide === index ? Colors.dark.primary : Colors.dark.surfaceTertiary,
              }}
            />
          ))}
        </HStack>

        <Button
          size="xl"
          className="mb-5 rounded-2xl border"
          style={{
            backgroundColor: Colors.dark.primary,
            borderColor: "rgba(255,255,255,0.22)",
            shadowColor: Colors.dark.primary,
            shadowOpacity: 0.45,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 8 },
          }}
          onPress={() => router.replace("/(tabs)")}
        >
          <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
            {screenCopy.onboarding.primaryAction}
          </ButtonText>
          <ChevronRight color={Colors.dark.primaryForeground} size={20} />
        </Button>

        <HStack className="gap-3">
          {Platform.OS === "web" ? (
            <>
              {createElement(
                "button",
                {
                  "aria-label": "Create account",
                  "data-testid": "onboarding-create-account",
                  onClick: () => router.push("/auth?mode=signup"),
                  onClickCapture: () => router.push("/auth?mode=signup"),
                  onMouseUp: () => router.push("/auth?mode=signup"),
                  onMouseUpCapture: () => router.push("/auth?mode=signup"),
                  onPointerUp: () => router.push("/auth?mode=signup"),
                  onPointerUpCapture: () => router.push("/auth?mode=signup"),
                  style: {
                    backgroundColor: "transparent",
                    borderColor: `${Colors.dark.primary}66`,
                    borderRadius: 16,
                    borderStyle: "solid",
                    borderWidth: 1,
                    color: Colors.dark.primary,
                    cursor: "pointer",
                    flex: 1,
                    fontWeight: 700,
                    minHeight: 44,
                    paddingInline: 16,
                  },
                  testid: "onboarding-create-account",
                  type: "button",
                },
                "Create account",
              )}
              {createElement(
                "button",
                {
                  "aria-label": "Sign in",
                  "data-testid": "onboarding-sign-in",
                  onClick: () => router.push("/auth?mode=signin"),
                  onClickCapture: () => router.push("/auth?mode=signin"),
                  onMouseUp: () => router.push("/auth?mode=signin"),
                  onMouseUpCapture: () => router.push("/auth?mode=signin"),
                  onPointerUp: () => router.push("/auth?mode=signin"),
                  onPointerUpCapture: () => router.push("/auth?mode=signin"),
                  style: {
                    backgroundColor: "transparent",
                    borderColor: `${Colors.dark.textSecondary}55`,
                    borderRadius: 16,
                    borderStyle: "solid",
                    borderWidth: 1,
                    color: Colors.dark.textSecondary,
                    cursor: "pointer",
                    flex: 1,
                    fontWeight: 700,
                    minHeight: 44,
                    paddingInline: 16,
                  },
                  testid: "onboarding-sign-in",
                  type: "button",
                },
                "Sign in",
              )}
            </>
          ) : (
            <>
              <Pressable
                accessibilityRole="button"
                className="min-h-11 flex-1 items-center justify-center rounded-2xl border px-4"
                testID="onboarding-create-account"
                style={{ borderColor: `${Colors.dark.primary}66` }}
                onPress={() => router.push("/auth?mode=signup")}
                {...createAccountHandlers}
              >
                <Text className="font-bold" style={{ color: Colors.dark.primary }}>
                  Create account
                </Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                className="min-h-11 flex-1 items-center justify-center rounded-2xl border px-4"
                testID="onboarding-sign-in"
                style={{ borderColor: `${Colors.dark.textSecondary}55` }}
                onPress={() => router.push("/auth?mode=signin")}
                {...signInHandlers}
              >
                <Text className="font-bold" style={{ color: Colors.dark.textSecondary }}>
                  Sign in
                </Text>
              </Pressable>
            </>
          )}
        </HStack>
        <TouchableOpacity className="mt-4" onPress={() => router.replace("/(tabs)")}>
          <Text className="text-center" style={{ color: Colors.dark.textSecondary }}>
            Continue locally without an account
          </Text>
        </TouchableOpacity>
      </VStack>
    </SafeAreaView>
  )
}
